import { Link, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { whatsappUrl } from "@/lib/site";
import iconAsset from "@/assets/svg-icon.png.asset.json";
import { SiteSearch } from "@/components/SiteSearch";

const chevron = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden className="opacity-70 transition-transform group-hover:rotate-180"><path d="m6 9 6 6 6-6" /></svg>
);

const linkBase =
  "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-medium tracking-tight leading-none transition-all";

function NavLink({ to, children, className = linkBase }: { to: string; children: React.ReactNode; className?: string }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const active = pathname === to || pathname.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      className={`${className} ${active ? "text-foreground bg-ink/[0.06] ring-1 ring-ink/10" : "text-muted-foreground hover:text-foreground hover:bg-ink/[0.04]"}`}
    >
      {children}
    </Link>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-ink/12 bg-background/70 backdrop-blur-xl supports-[backdrop-filter]:bg-background/55 shadow-[0_1px_0_0_oklch(1_0_0_/_0.04),0_10px_30px_-20px_oklch(0_0_0_/_0.6)]">
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent opacity-60" />
      <div className="container-svg flex h-[68px] items-center justify-between gap-4">
        <Link to="/" className="group flex items-center gap-3" aria-label="SVG Multimídia — Início">
          <img
            src={iconAsset.url}
            alt=""
            aria-hidden="true"
            width={40}
            height={40}
            className="h-10 w-10 rounded-xl bg-white/95 p-1.5 shadow-[var(--shadow-glow)] ring-1 ring-primary/30 transition-transform duration-300 group-hover:scale-105 group-hover:rotate-[-2deg]"
          />
          <span className="font-display text-lg font-bold tracking-tight leading-none">
            SVG <span className="text-gradient">Multimídia</span>
          </span>
        </Link>
        <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Principal">
          {/* CRM SVG */}
          <div className="group relative">
            <NavLink to="/crm-yt">
              CRM SVG
              {chevron}
            </NavLink>
            <div className="invisible absolute left-0 top-full z-50 min-w-[280px] translate-y-1 opacity-0 transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
              <div className="mt-2 rounded-2xl border border-ink/10 bg-background/95 p-2 shadow-2xl backdrop-blur-xl">
                <Link to="/crm-yt" className="block rounded-xl px-3 py-2 text-sm hover:bg-ink/[0.05]">
                  <span className="block font-semibold">CRM SVG com IA</span>
                  <span className="block text-xs text-muted-foreground">Conheça o produto e recursos</span>
                </Link>
                <Link to="/crm-para-whatsapp" className="block rounded-xl px-3 py-2 text-sm hover:bg-ink/[0.05]">
                  <span className="block font-semibold">CRM para WhatsApp</span>
                  <span className="block text-xs text-muted-foreground">Fale agora com a IA de atendimento</span>
                </Link>
                <Link to="/automacao-comercial" className="block rounded-xl px-3 py-2 text-sm hover:bg-ink/[0.05]">
                  <span className="block font-semibold">Automação de atendimento</span>
                  <span className="block text-xs text-muted-foreground">Atendimento e vendas automatizados com IA</span>
                </Link>
                <Link to="/consultoria-de-automacao-comercial" className="block rounded-xl px-3 py-2 text-sm hover:bg-ink/[0.05]">
                  <span className="block font-semibold">Consultoria comercial</span>
                  <span className="block text-xs text-muted-foreground">Processos, funil e rotina de vendas</span>
                </Link>
                <Link to="/suporte-crm" className="block rounded-xl px-3 py-2 text-sm hover:bg-ink/[0.05]">
                  <span className="block font-semibold">Suporte e Treinamento</span>
                  <span className="block text-xs text-muted-foreground">Tutoriais, trilha de estudos e IA de ajuda</span>
                </Link>
                <a href="https://app.svgmultimidia.com.br/" target="_blank" rel="noopener" className="block rounded-xl px-3 py-2 text-sm hover:bg-ink/[0.05]">
                  <span className="block font-semibold">Acesso ao Sistema</span>
                  <span className="block text-xs text-muted-foreground">Entrar no CRM oficial (app.svgmultimidia.com.br)</span>
                </a>
                <Link to="/venda-no-whatsapp-metodo-conversa" className="block rounded-xl px-3 py-2 text-sm hover:bg-ink/[0.05]">
                  <span className="block font-semibold">Método CONVERSA</span>
                  <span className="block text-xs text-muted-foreground">Venda no WhatsApp com conversas que convertem</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Sobre + Equipe */}
          <div className="group relative">
            <NavLink to="/sobre">
              Sobre nós
              {chevron}
            </NavLink>
            <div className="invisible absolute left-0 top-full z-50 min-w-[260px] translate-y-1 opacity-0 transition-all duration-150 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
              <div className="mt-2 rounded-2xl border border-ink/10 bg-background/95 p-2 shadow-2xl backdrop-blur-xl">
                <Link to="/sobre" className="block rounded-xl px-3 py-2 text-sm hover:bg-ink/[0.05]">
                  <span className="block font-semibold">Sobre a SVG</span>
                  <span className="block text-xs text-muted-foreground">Nossa história, números e diferenciais</span>
                </Link>
                <Link to="/equipe" className="block rounded-xl px-3 py-2 text-sm hover:bg-ink/[0.05]">
                  <span className="block font-semibold">Nossa Equipe</span>
                  <span className="block text-xs text-muted-foreground">Conheça os especialistas por trás dos resultados</span>
                </Link>
              </div>
            </div>
          </div>

          <NavLink to="/blog">Blog</NavLink>
          <NavLink to="/contato">Contato</NavLink>
        </nav>
        <div className="flex items-center gap-2">
          <a
            href={whatsappUrl()}
            target="_blank"
            rel="noopener"
            aria-label="Solicitar diagnóstico gratuito no WhatsApp"
            className="group relative hidden md:inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-[0_10px_30px_-12px_oklch(0.62_0.22_258_/_0.7)] ring-1 ring-ink/15 transition-transform duration-200 hover:-translate-y-[1px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            style={{ background: "linear-gradient(135deg, oklch(0.66 0.22 258) 0%, oklch(0.55 0.22 245) 100%)" }}
          >
            <span aria-hidden className="absolute inset-0 rounded-full bg-gradient-to-r from-ink/15 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
            </svg>
            <span className="relative">Diagnóstico gratuito</span>
          </a>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-xl border border-ink/10 shadow-[var(--shadow-soft)] bg-ink/[0.04] backdrop-blur"
            aria-label="Abrir menu"
            aria-expanded={open}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              {open ? <path d="M6 6l12 12M6 18L18 6" /> : <><path d="M4 7h16" /><path d="M4 12h16" /><path d="M4 17h16" /></>}
            </svg>
          </button>
        </div>
      </div>
      <div className="hidden md:block border-t border-ink/12 bg-background/40">
        <div className="container-svg py-2 flex justify-center">
          <div className="w-full max-w-2xl">
            <SiteSearch compact />
          </div>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-ink/10 bg-background/95 backdrop-blur-xl">
          <nav className="container-svg flex flex-col py-3" aria-label="Menu mobile">
            <div className="mb-2"><SiteSearch compact /></div>
            <Link to="/crm-yt" onClick={() => setOpen(false)} className="rounded-lg px-2 py-3 text-sm font-medium text-foreground hover:bg-ink/[0.04]">CRM SVG com IA</Link>
            <Link to="/crm-para-whatsapp" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:bg-ink/[0.04] hover:text-foreground">↳ CRM para WhatsApp</Link>
            <Link to="/automacao-comercial" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:bg-ink/[0.04] hover:text-foreground">↳ Automação de atendimento</Link>
            <Link to="/consultoria-de-automacao-comercial" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:bg-ink/[0.04] hover:text-foreground">↳ Consultoria comercial</Link>
            <Link to="/suporte-crm" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:bg-ink/[0.04] hover:text-foreground">↳ Suporte e Treinamento</Link>
            <a href="https://app.svgmultimidia.com.br/" target="_blank" rel="noopener" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:bg-ink/[0.04] hover:text-foreground">↳ Acesso ao Sistema</a>
            <Link to="/venda-no-whatsapp-metodo-conversa" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:bg-ink/[0.04] hover:text-foreground">↳ Método CONVERSA</Link>
            <Link to="/blog" onClick={() => setOpen(false)} className="rounded-lg px-2 py-3 text-sm font-medium text-foreground hover:bg-ink/[0.04]">Blog</Link>
            <Link to="/sobre" onClick={() => setOpen(false)} className="rounded-lg px-2 py-3 text-sm font-medium text-foreground hover:bg-ink/[0.04]">Sobre nós</Link>
            <Link to="/equipe" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-muted-foreground hover:bg-ink/[0.04] hover:text-foreground">↳ Nossa Equipe</Link>
            <Link to="/contato" onClick={() => setOpen(false)} className="rounded-lg px-2 py-3 text-sm font-medium text-foreground hover:bg-ink/[0.04]">Contato</Link>
            <a href={whatsappUrl()} target="_blank" rel="noopener" className="btn-primary mt-2">Diagnóstico gratuito</a>
          </nav>
        </div>
      )}
    </header>
  );
}
